import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import campaignService, { isCampaignActive } from '../../services/campaignService';
import paymentService from '../../services/paymentService';
import LoadingSpinner from '../../components/LoadingSpinner';
import QuickStartGuide from '../../components/common/QuickStartGuide';

// Recharts
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';

const STATUS_COLORS = {
  active: '#3B5BA5',
  draft: '#9ca3af',
  completed: '#10b981',
  paused: '#f59e0b',
};

const formatCurrency = (value) => {
  if (value >= 1000000) return `₱${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `₱${(value / 1000).toFixed(1)}K`;
  return `₱${value.toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatNumber = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toString();
};

const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const daysUntil = (timestamp) => {
  if (!timestamp) return null;
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return Math.round((date - now) / (1000 * 60 * 60 * 24));
};

// Stat card — clean, minimal, shadcn-style
const StatCard = ({ label, value, sublabel, trend }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-sm transition-shadow duration-200">
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
    <div className="mt-2 flex items-baseline gap-2">
      <span className="text-2xl font-bold text-gray-900">{value}</span>
      {trend && (
        <span className={`text-xs font-medium ${trend > 0 ? 'text-green-600' : 'text-gray-400'}`}>
          {trend > 0 ? `+${trend}%` : ''}
        </span>
      )}
    </div>
    {sublabel && <p className="mt-1 text-xs text-gray-400">{sublabel}</p>}
  </div>
);

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [profileCompletion, setProfileCompletion] = useState(100);
  const [showProfileBanner, setShowProfileBanner] = useState(false);

  // Raw data
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [pendingProofs, setPendingProofs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [recentApplications, setRecentApplications] = useState([]);

  // Derived stats
  const [stats, setStats] = useState({
    activeCampaigns: 0,
    totalCampaigns: 0,
    engagedInfluencers: 0,
    pendingReviews: 0,
    totalReach: 0,
    totalSpent: 0,
    pendingPayments: 0,
    totalApplications: 0,
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!currentUser?.uid) return;

        // 1. Fetch all campaigns for the brand
        const allCampaignsData = await campaignService.getCampaignsByBrandId(currentUser.uid);
        setAllCampaigns(allCampaignsData);

        const trulyActive = allCampaignsData.filter(
          (c) => c.status === 'active' && isCampaignActive(c)
        );
        setActiveCampaigns(trulyActive);

        const campaignIds = allCampaignsData.map((c) => c.id);

        // 2. Fetch participants, proofs, payments in parallel
        let participantsData = [];
        let pendingProofsData = [];
        let paymentsData = [];

        if (campaignIds.length > 0) {
          // Firestore "in" query supports max 30 items
          const batchedIds = campaignIds.length > 30
            ? campaignIds.slice(0, 30)
            : campaignIds;

          const [participantsSnap, proofsSnap, paymentsDataResult] = await Promise.all([
            getDocs(query(
              collection(db, 'campaignParticipants'),
              where('campaignId', 'in', batchedIds)
            )),
            getDocs(query(
              collection(db, 'proofs'),
              where('campaignId', 'in', batchedIds),
              where('status', '==', 'pending')
            )),
            paymentService.getPaymentsByBrandId(currentUser.uid),
          ]);

          participantsData = participantsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          pendingProofsData = proofsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          paymentsData = paymentsDataResult;
        }

        setParticipants(participantsData);
        setPendingProofs(pendingProofsData);
        setPayments(paymentsData);

        // 3. Recent applications (pending status, sorted by date)
        const recentApps = participantsData
          .filter((p) => p.status === 'pending')
          .sort((a, b) => {
            const aTime = a.appliedAt?.seconds || a.createdAt?.seconds || 0;
            const bTime = b.appliedAt?.seconds || b.createdAt?.seconds || 0;
            return bTime - aTime;
          })
          .slice(0, 5);
        setRecentApplications(recentApps);

        // 4. Calculate stats
        const engagedInfluencers = participantsData.filter((p) => p.status === 'approved').length;
        const totalReach = allCampaignsData.reduce(
          (sum, c) => sum + (c.analytics?.totalReach || c.metrics?.totalReach || 0), 0
        );
        const totalSpent = paymentsData
          .filter((p) => p.status === 'completed')
          .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
        const pendingPayments = paymentsData
          .filter((p) => p.status === 'pending' || p.status === 'processing')
          .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
        const totalApplications = allCampaignsData.reduce(
          (sum, c) => sum + (c.analytics?.applications || 0), 0
        );

        setStats({
          activeCampaigns: trulyActive.length,
          totalCampaigns: allCampaignsData.length,
          engagedInfluencers,
          pendingReviews: pendingProofsData.length,
          totalReach,
          totalSpent,
          pendingPayments,
          totalApplications,
        });

        // 5. Quick start guide for new brands
        const hasSeenGuide = localStorage.getItem('quickStartGuide_brand_seen');
        if (!hasSeenGuide && allCampaignsData.length <= 1) {
          setShowQuickStart(true);
        }
      } catch (err) {
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [currentUser?.uid]);

  // Fetch brand profile and calculate completion percentage
  useEffect(() => {
    const fetchProfileCompletion = async () => {
      if (!currentUser?.uid) return;
      try {
        const profileDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!profileDoc.exists()) return;
        const data = profileDoc.data();

        const checks = [
          !!(data.companyName || data.name),
          !!(data.description || data.bio),
          !!(data.profilePicture || data.logo),
          !!(data.industry || data.category),
          !!data.website,
          !!data.contactEmail,
          !!data.phoneNumber,
          !!(data.address || data.location),
          !!(data.socialMedia && Object.keys(data.socialMedia).length > 0),
          !!(data.brandGuidelines || data.guidelines),
        ];

        const completed = checks.filter(Boolean).length;
        const percentage = Math.round((completed / checks.length) * 100);
        setProfileCompletion(percentage);

        const dismissed = localStorage.getItem('profileCompleteDismissed_brand');
        setShowProfileBanner(!dismissed && percentage < 100);
      } catch (err) {
        // Silently fail — banner is non-critical
      }
    };

    fetchProfileCompletion();
  }, [currentUser?.uid]);

  // Chart data: campaign status distribution (pie/donut)
  const campaignStatusData = useMemo(() => {
    const counts = {};
    allCampaigns.forEach((c) => {
      const status = c.status || 'draft';
      counts[status] = (counts[status] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [allCampaigns]);

  // Chart data: monthly spending (area chart)
  const spendingData = useMemo(() => {
    const monthlyMap = {};
    payments
      .filter((p) => p.status === 'completed')
      .forEach((payment) => {
        let date;
        if (payment.createdAt?.seconds) {
          date = new Date(payment.createdAt.seconds * 1000);
        } else if (payment.paidAt?.seconds) {
          date = new Date(payment.paidAt.seconds * 1000);
        } else if (payment.createdAt instanceof Date) {
          date = payment.createdAt;
        } else {
          return;
        }
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthlyMap[key] = (monthlyMap[key] || 0) + (Number(payment.amount) || 0);
      });

    const now = new Date();
    const months = [];
    for (let i = 7; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en', { month: 'short' });
      months.push({ month: label, spending: Math.round(monthlyMap[key] || 0) });
    }
    return months;
  }, [payments]);

  // Chart data: applications per campaign (bar chart)
  const applicationsData = useMemo(() => {
    return allCampaigns
      .slice()
      .sort((a, b) => (b.analytics?.applications || 0) - (a.analytics?.applications || 0))
      .slice(0, 5)
      .map((c) => ({
        name: (c.name || c.title || 'Untitled').length > 15
          ? (c.name || c.title || 'Untitled').substring(0, 15) + '...'
          : (c.name || c.title || 'Untitled'),
        applications: c.analytics?.applications || 0,
        approved: c.analytics?.approvedApplications || 0,
      }));
  }, [allCampaigns]);

  // Upcoming deadlines
  const upcomingDeadlines = useMemo(() => {
    return activeCampaigns
      .map((c) => ({
        ...c,
        daysLeft: daysUntil(c.endDate || c.deadline),
      }))
      .filter((c) => c.daysLeft !== null && c.daysLeft >= 0)
      .sort((a, b) => a.daysLeft - b.daysLeft)
      .slice(0, 5);
  }, [activeCampaigns]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Brand Dashboard</h2>
          <p className="text-gray-600">Overview of your campaigns, influencers, and performance</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-1">Something went wrong</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Brand Dashboard</h2>
        <p className="text-gray-600">Overview of your campaigns, influencers, and performance</p>
      </div>

      {/* Profile Completion Banner */}
      {showProfileBanner && profileCompletion < 100 && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-blue-900">
                  Your profile is {profileCompletion}% complete
                </p>
                <Link
                  to="/brand/profile"
                  className="text-sm font-medium text-blue-700 hover:text-blue-800 underline"
                >
                  Complete your profile
                </Link>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${profileCompletion}%` }}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowProfileBanner(false);
                localStorage.setItem('profileCompleteDismissed_brand', 'true');
              }}
              className="text-blue-400 hover:text-blue-600 transition-colors"
              aria-label="Dismiss"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Quick Start Guide */}
      {showQuickStart && (
        <QuickStartGuide
          userType="brand"
          onClose={() => {
            setShowQuickStart(false);
            localStorage.setItem('quickStartGuide_brand_seen', 'true');
          }}
        />
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Active Campaigns"
          value={stats.activeCampaigns}
          sublabel={`${stats.totalCampaigns} total campaigns`}
        />
        <StatCard
          label="Engaged Influencers"
          value={stats.engagedInfluencers}
          sublabel={`${stats.totalApplications} applications received`}
        />
        <StatCard
          label="Pending Reviews"
          value={stats.pendingReviews}
          sublabel="Content awaiting approval"
        />
        <StatCard
          label="Total Reach"
          value={formatNumber(stats.totalReach)}
          sublabel="Across all campaigns"
        />
      </div>

      {/* Secondary Stats Row — Finance */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard
          label="Total Spent"
          value={formatCurrency(stats.totalSpent)}
          sublabel="Completed payments"
        />
        <StatCard
          label="Pending Payments"
          value={formatCurrency(stats.pendingPayments)}
          sublabel="Awaiting processing"
        />
        <StatCard
          label="Avg Reach / Campaign"
          value={stats.activeCampaigns > 0 ? formatNumber(Math.round(stats.totalReach / stats.activeCampaigns)) : '0'}
          sublabel="Based on active campaigns"
        />
      </div>

      {/* Charts Section — Two Column */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Spending Over Time — Area Chart (2/3 width) */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-800">Spending Overview</h3>
            <p className="text-sm text-gray-500">Monthly campaign payments (last 8 months)</p>
          </div>
          {spendingData.some((d) => d.spending > 0) ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={spendingData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="spendingGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B5BA5" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#3B5BA5" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => (v >= 1000 ? `₱${(v / 1000).toFixed(0)}k` : `₱${v}`)}
                />
                <Tooltip
                  formatter={(value) => [formatCurrency(value), 'Spending']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
                />
                <Area
                  type="monotone"
                  dataKey="spending"
                  stroke="#3B5BA5"
                  strokeWidth={2}
                  fill="url(#spendingGradient)"
                  dot={{ fill: '#3B5BA5', r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center">
              <p className="text-sm text-gray-400">No spending data yet. Completed payments will appear here.</p>
            </div>
          )}
        </div>

        {/* Campaign Status — Donut Chart (1/3 width) */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-800">Campaign Status</h3>
            <p className="text-sm text-gray-500">Distribution by status</p>
          </div>
          {campaignStatusData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={campaignStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {campaignStatusData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={STATUS_COLORS[entry.name] || '#9ca3af'}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {campaignStatusData.map((entry) => (
                  <div key={entry.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: STATUS_COLORS[entry.name] || '#9ca3af' }}
                      />
                      <span className="text-gray-600 capitalize">{entry.name}</span>
                    </div>
                    <span className="font-medium text-gray-900">{entry.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-[280px] flex items-center justify-center">
              <p className="text-sm text-gray-400">No campaigns yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Applications Bar Chart + Upcoming Deadlines — Two Column */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top Campaigns by Applications — Bar Chart */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-800">Top Campaigns by Applications</h3>
            <p className="text-sm text-gray-500">Applications vs. approved (top 5)</p>
          </div>
          {applicationsData.some((d) => d.applications > 0) ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={applicationsData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  angle={-15}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
                  cursor={{ fill: '#f9fafb' }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="applications" fill="#3B5BA5" radius={[4, 4, 0, 0]} name="Applications" />
                <Bar dataKey="approved" fill="#10b981" radius={[4, 4, 0, 0]} name="Approved" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center">
              <p className="text-sm text-gray-400">No application data yet.</p>
            </div>
          )}
        </div>

        {/* Upcoming Deadlines */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-800">Upcoming Deadlines</h3>
              <p className="text-sm text-gray-500">Campaigns ending soon</p>
            </div>
            <Link to="/brand/campaigns" className="text-sm text-primary hover:text-primary-dark">
              View all
            </Link>
          </div>
          {upcomingDeadlines.length > 0 ? (
            <div className="space-y-3">
              {upcomingDeadlines.map((campaign) => {
                const days = campaign.daysLeft;
                const urgent = days <= 3;
                const warning = days <= 7 && days > 3;
                return (
                  <Link
                    key={campaign.id}
                    to={`/brand/campaigns/${campaign.id}`}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {campaign.name || campaign.title || 'Untitled'}
                      </p>
                      <p className="text-xs text-gray-400">{formatDate(campaign.endDate || campaign.deadline)}</p>
                    </div>
                    <span
                      className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                        urgent
                          ? 'bg-red-100 text-red-700'
                          : warning
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {days === 0 ? 'Today' : days === 1 ? '1 day left' : `${days} days left`}
                    </span>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="h-[280px] flex items-center justify-center">
              <p className="text-sm text-gray-400">No upcoming deadlines.</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Link
          to="/brand/campaigns/create"
          className="block bg-white rounded-lg border border-gray-200 p-5 hover:shadow-sm transition-shadow duration-200"
        >
          <h3 className="text-sm font-medium text-gray-900 mb-1">Create Campaign</h3>
          <p className="text-sm text-gray-500">Launch a new influencer campaign</p>
        </Link>
        <Link
          to="/brand/content-approval"
          className="block bg-white rounded-lg border border-gray-200 p-5 hover:shadow-sm transition-shadow duration-200"
        >
          <h3 className="text-sm font-medium text-gray-900 mb-1">Review Content</h3>
          <p className="text-sm text-gray-500">
            {stats.pendingReviews > 0 ? `${stats.pendingReviews} pending review` : 'Approve or request revisions'}
          </p>
        </Link>
        <Link
          to="/brand/analytics"
          className="block bg-white rounded-lg border border-gray-200 p-5 hover:shadow-sm transition-shadow duration-200"
        >
          <h3 className="text-sm font-medium text-gray-900 mb-1">View Analytics</h3>
          <p className="text-sm text-gray-500">Track campaign performance</p>
        </Link>
      </div>

      {/* Active Campaigns Table */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-800">Active Campaigns</h3>
          <Link to="/brand/campaigns" className="text-sm text-primary hover:text-primary-dark">
            View all
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Influencers</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submissions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approved</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reach</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Engagement</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activeCampaigns.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-sm text-gray-500">
                    No active campaigns found.{' '}
                    <Link to="/brand/campaigns/create" className="text-primary hover:text-primary-dark font-medium">
                      Create one
                    </Link>
                  </td>
                </tr>
              ) : (
                activeCampaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/brand/campaigns/${campaign.id}`} className="text-primary hover:text-primary-dark font-medium">
                        {campaign.name || campaign.title || 'Untitled'}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {campaign.filledSpots || 0}
                      {campaign.totalSpots ? ` / ${campaign.totalSpots}` : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {campaign.analytics?.submittedContent || campaign.analytics?.applications || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {campaign.analytics?.approvedContent || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatNumber(campaign.analytics?.totalReach || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {campaign.analytics?.totalEngagement
                        ? `${campaign.analytics.totalEngagement}%`
                        : '0%'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Applications */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-800">Recent Applications</h3>
          <Link to="/brand/applications" className="text-sm text-primary hover:text-primary-dark">
            View all
          </Link>
        </div>
        {recentApplications.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {recentApplications.map((app) => (
              <div key={app.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {app.influencerName || app.displayName || 'Unknown influencer'}
                  </p>
                  <p className="text-xs text-gray-400">
                    Applied {formatDate(app.appliedAt || app.createdAt)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                    Pending
                  </span>
                  <Link
                    to={`/brand/campaigns/${app.campaignId}`}
                    className="text-sm text-primary hover:text-primary-dark font-medium"
                  >
                    Review
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-6 py-8 text-center text-sm text-gray-500">
            No pending applications.
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
